/**
 * Dropdown menu (`role="menu"`) opened from any trigger via a render prop:
 *
 *   <Menu label="Row actions" items={[{ label: 'Edit', onSelect: edit }, 'separator', …]}
 *         trigger={(props) => <IconButton {...props} label="Actions" icon={<MoreHorizontal />} />} />
 *
 * Keyboard: ↓/↑/Home/End move, Enter/Space activate, Escape/Tab close. Items can be
 * actions (`onSelect`) or links (`to`).
 */
import clsx from 'clsx';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router';
import { KeyHint } from './KeyHint';
import type { Placement } from './floating';
import { Popover } from './Popover';
import styles from './Menu.module.css';

export interface MenuAction {
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  /** Navigate instead of calling `onSelect`. */
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Hotkey hint shown right-aligned. */
  shortcut?: string;
  /** Marks the item as the current choice (`aria-checked`, check style). */
  checked?: boolean;
}

export type MenuEntry = MenuAction | 'separator' | { heading: ReactNode };

export interface MenuTriggerProps {
  ref: Ref<HTMLButtonElement>;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
}

export interface MenuProps {
  trigger: (props: MenuTriggerProps) => ReactNode;
  items: readonly MenuEntry[];
  /** Accessible name of the menu. */
  label: string;
  placement?: Placement;
  className?: string;
}

function isAction(entry: MenuEntry): entry is MenuAction {
  return typeof entry === 'object' && 'label' in entry;
}

export function Menu({ trigger, items, label, placement = 'bottom-end', className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const focusItem = (index: 'first' | 'last') => {
    requestAnimationFrame(() => {
      const nodes = document
        .getElementById(menuId)
        ?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])');
      if (!nodes?.length) return;
      (index === 'first' ? nodes[0] : nodes[nodes.length - 1])?.focus();
    });
  };

  const close = () => setOpen(false);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const nodes = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])'),
    );
    const index = nodes.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = (index + 1) % nodes.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + nodes.length) % nodes.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = nodes.length - 1;
    else if (event.key === 'Tab') close();
    if (next !== null) {
      event.preventDefault();
      nodes[next]?.focus();
    }
  };

  return (
    <>
      {trigger({
        ref: anchorRef,
        onClick: () => setOpen((value) => !value),
        onKeyDown: (event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            focusItem(event.key === 'ArrowDown' ? 'first' : 'last');
          }
        },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
      })}
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement={placement}
        role="menu"
        id={menuId}
        aria-label={label}
        className={clsx(styles.menu, className)}
      >
        <div role="none" onKeyDown={onMenuKeyDown}>
          {items.map((entry, i) => {
            if (entry === 'separator') return <div key={i} role="separator" className={styles.separator} />;
            if (!isAction(entry)) {
              return (
                <div key={i} className={styles.heading} role="presentation">
                  {entry.heading}
                </div>
              );
            }
            const content = (
              <>
                <span className={styles.icon} aria-hidden>
                  {entry.icon}
                </span>
                <span className={styles.label}>{entry.label}</span>
                {entry.shortcut && <KeyHint combo={entry.shortcut} className={styles.shortcut} />}
              </>
            );
            const className = clsx(
              styles.item,
              entry.danger && styles.danger,
              entry.checked && styles.checked,
            );
            const role = entry.checked === undefined ? 'menuitem' : 'menuitemradio';
            if (entry.to && !entry.disabled) {
              return (
                <Link
                  key={i}
                  to={entry.to}
                  role={role}
                  aria-checked={entry.checked}
                  className={className}
                  onClick={close}
                >
                  {content}
                </Link>
              );
            }
            return (
              <button
                key={i}
                type="button"
                role={role}
                aria-checked={entry.checked}
                aria-disabled={entry.disabled || undefined}
                className={className}
                onClick={() => {
                  if (entry.disabled) return;
                  close();
                  anchorRef.current?.focus();
                  entry.onSelect?.();
                }}
              >
                {content}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
